// src/components/library/FilterPanel.js
import React, { useState, useEffect } from 'react';
import { Filter, X, Search } from 'lucide-react';

const FilterPanel = ({ 
    currentFilters = {}, 
    setFilter, 
    clearFilter,
    categoryTitle 
}) => {
    
    const filterFields = [
        { key: 'publisher__title', label: 'نام ناشر', type: 'text' },
        { key: 'author__name', label: 'نام نویسنده', type: 'text' },
        { key: 'translator__name', label: 'نام مترجم', type: 'text' },
        { key: 'ordering', label: 'مرتب‌سازی', type: 'select', options: [
            { value: '-created_at', name: 'جدیدترین' },
            { value: 'created_at', name: 'قدیمی‌ترین' },
            { value: 'title', name: 'عنوان (الفبا)' },
            { value: '-base_price', name: 'قیمت (گرانترین)' },
            { value: 'base_price', name: 'قیمت (ارزانترین)' },
        ]},
    ];
    
    const TEXT_FILTER_KEYS = ['publisher__title', 'author__name', 'translator__name'];

    const [localFilterValues, setLocalFilterValues] = useState(() => {
        const initialTextFilters = {};
        TEXT_FILTER_KEYS.forEach(key => {
            initialTextFilters[key] = currentFilters[key] || ''; 
        });
        return initialTextFilters;
    });
    
    // همگام‌سازی state محلی با تغییرات URL
    useEffect(() => {
        setLocalFilterValues(prevLocalValues => {
            let needsSync = false;
            const newLocalValues = { ...prevLocalValues }; 
            TEXT_FILTER_KEYS.forEach(key => {
                const currentUrlValue = currentFilters[key] || '';
                if (currentUrlValue !== prevLocalValues[key]) {
                    needsSync = true;
                    newLocalValues[key] = currentUrlValue;
                }
            });
            return needsSync ? newLocalValues : prevLocalValues;
        });
    }, [currentFilters]);

    const handleTextInputChange = (e) => {
        const { name, value } = e.target;
        setLocalFilterValues(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    const handleSelectChange = (e) => {
        const { name, value } = e.target;
        // مرتب‌سازی مستقیماً اعمال می‌شود (بدون نیاز به دکمه جستجو)
        setFilter({ [name]: value });
    };
    
    // 🔧 اصلاح مهم: ارسال تمام فیلترهای متنی به صورت یک شیء
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const filters = {};
        TEXT_FILTER_KEYS.forEach(key => {
            const val = localFilterValues[key];
            if (val && val.trim()) filters[key] = val.trim();
        });
        // همچنین مرتب‌سازی فعلی را هم حفظ کن (اگر در state جداگانه باشد)
        const ordering = currentFilters.ordering;
        if (ordering) filters.ordering = ordering;
        setFilter(filters);
    };

    const getFilterLabel = (key) => {
        if (key === 'category__slug') return 'دسته‌بندی';
        const field = filterFields.find(f => f.key === key);
        return field ? field.label : key.replace(/__/g, ' ').replace('title', 'عنوان').replace('slug', 'اسلاگ');
    };
    
    const activeFilters = Object.entries(currentFilters).filter(([key, value]) => value && key !== 'ordering');
    const categoryFilterSlug = currentFilters['category__slug'];

    return (
        <div className="space-y-4">
            {categoryFilterSlug && categoryTitle && ( 
                <div className="p-2">
                    <p className="text-l text-gray-700 font-bold">
                        <Filter size={16} className="ml-1 inline-block" /> 
                        <span className="font-bold mr-1">{categoryTitle}</span>
                    </p>
                </div>
            )}

            <form onSubmit={handleSearchSubmit} className="bg-white p-4 rounded-xl shadow-md border border-gray-200 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    {filterFields.map((field) => (
                        <div key={field.key} className="space-y-1 col-span-1">
                            <label className="text-[11px] font-medium text-gray-700 block">{field.label}:</label>
                            {field.type === 'select' ? (
                                <select 
                                    name={field.key} 
                                    onChange={handleSelectChange} 
                                    value={currentFilters[field.key] || ''}
                                    className="w-full p-2 border border-gray-300 rounded-lg text-[11px] focus:ring-indigo-500 focus:border-indigo-500"
                                >
                                    <option value="">(انتخاب کنید)</option>
                                    {field.options.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.name}</option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    name={field.key}
                                    placeholder={`جستجو...`}
                                    onChange={handleTextInputChange} 
                                    value={localFilterValues[field.key] || ''} 
                                    className="w-full p-2 border border-gray-300 rounded-lg text-[11px] focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            )}
                        </div>
                    ))}
                </div>
                
                <button 
                    type="submit" 
                    className="w-full flex items-center justify-center p-2 bg-white text-purple-700 rounded-lg border border-purple-700 hover:bg-indigo-700 transition-colors text-white-700"
                >
                    <Search size={18} className="ml-2" />
                    اعمال فیلتر (Enter)
                </button>
                
                {activeFilters.length > 0 && (
                    <div className="pt-2 border-t border-gray-100">
                        <h4 className="text-[11px] font-medium mb-2 text-gray-700">فیلترهای سفارشی:</h4>
                        <div className="flex flex-wrap gap-2">
                            {activeFilters.map(([key, value]) => (
                                key !== 'category__slug' && (
                                    <span key={key} className="flex items-center text-xs font-medium px-3 py-1 rounded-full bg-indigo-100 text-indigo-700">
                                        {getFilterLabel(key)}: {value}
                                        <button 
                                            type="button" 
                                            onClick={() => clearFilter(key)} 
                                            className="mr-1 text-indigo-500 hover:text-indigo-900"
                                        >
                                            <X size={12} />
                                        </button>
                                    </span>
                                )
                            ))}
                            <button 
                                type="button" 
                                onClick={() => clearFilter()} 
                                className="text-xs text-red-600 hover:text-red-800 font-medium pt-1"
                            >
                                پاک کردن همه فیلترها
                            </button>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

export default FilterPanel;